import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

/**
 * A `position: fixed` menu portalled to `document.body` and anchored to a
 * trigger's `getBoundingClientRect()`.
 *
 * WHY THIS EXISTS. Both menus in ModelManagementMainSection used to position
 * themselves with a hardcoded
 *
 *     bottom: window.innerHeight - rect.top + 8
 *
 * which always opens the menu UPWARD and has no flip and no clamp. That is fine
 * for a trigger near the bottom of a long list and wrong at the top of one: the
 * version menu is ~150px tall, so any trigger less than that from the top of the
 * viewport renders partly above `y = 0`. Because the element is `fixed`, the part
 * above the fold cannot be scrolled to — and the page's own scroll handler closes
 * the menu on the first wheel event, so there is no recovery either. The first
 * row of the first model is exactly where that bites.
 *
 * So the menu MEASURES ITSELF and then decides:
 *
 *   · it still prefers to open upward, which is what the list was designed
 *     around and what every existing screenshot shows;
 *   · it flips downward when there is not enough room above and more room below;
 *   · and either way it is clamped into the viewport, so neither edge can ever
 *     put an item out of reach.
 *
 * The measure happens in `useLayoutEffect`, which runs BEFORE the browser paints,
 * so there is no visible jump from the pre-measurement position. Until it has run
 * the menu is `visibility: hidden` rather than unmounted — it has to be in the
 * document to have a height at all, and `hidden` keeps it out of the first paint
 * while still laying it out.
 *
 * Callers keep closing the menu on scroll (the anchor rect goes stale the moment
 * the page moves), so this deliberately does not re-measure on scroll.
 */

/** Gap between the trigger and the menu, in CSS px. */
const GAP = 8;
/** Smallest distance the menu may sit from any viewport edge, in CSS px. */
const EDGE_MARGIN = 8;

const DropdownPortal = ({ anchorRect, className = "", children, menuRef }) => {
  const localRef = useRef(null);
  const [placement, setPlacement] = useState(null);

  useLayoutEffect(() => {
    const el = localRef.current;
    if (!el || !anchorRect) return;

    const { offsetHeight: height, offsetWidth: width } = el;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    const spaceAbove = anchorRect.top - GAP;
    const spaceBelow = viewportH - anchorRect.bottom - GAP;

    // Upward is the default; downward only when above genuinely cannot hold it
    // AND below can hold more. A menu taller than both gets clamped below.
    const openUp = spaceAbove >= height || spaceAbove >= spaceBelow;
    let top = openUp ? anchorRect.top - GAP - height : anchorRect.bottom + GAP;
    top = Math.max(EDGE_MARGIN, Math.min(top, viewportH - height - EDGE_MARGIN));

    // Right-aligned to the trigger, as before — then clamped, so a menu wider
    // than the space to its left cannot slide off the left edge.
    let left = anchorRect.right - width;
    left = Math.max(EDGE_MARGIN, Math.min(left, viewportW - width - EDGE_MARGIN));

    setPlacement({ top, left });
    // anchorRect is a fresh DOMRect per open, so identity is the right trigger.
  }, [anchorRect]);

  if (!anchorRect) return null;

  const setRefs = (node) => {
    localRef.current = node;
    if (typeof menuRef === "function") menuRef(node);
    else if (menuRef) menuRef.current = node;
  };

  return ReactDOM.createPortal(
    <div
      className={`labelflow-dropdown-portal ${className}`.trim()}
      ref={setRefs}
      style={{
        position: "fixed",
        // Before the measure, park it at the unclamped upward position rather
        // than at 0,0: if anything ever prevents the layout effect from running,
        // the fallback is the old behaviour and not a menu in the corner.
        top: placement ? placement.top : Math.max(EDGE_MARGIN, anchorRect.top - GAP),
        left: placement ? placement.left : anchorRect.left,
        visibility: placement ? "visible" : "hidden",
        zIndex: 1060,
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export default DropdownPortal;
